import React from 'react';

export type LiveStatusRegionProps = {
  /** Texto que se anuncia. Cadena vacía = región montada y en silencio. */
  message: string;
};

/**
 * Región de estado accesible (WCAG 4.1.3): anuncia de forma no intrusiva los cambios que la vista
 * no puede comunicar de otra manera (se ha cargado otra página, la lista ha cambiado de filtro,
 * no hay resultados).
 *
 * Dos condiciones la hacen funcionar, y las dos se rompen con facilidad:
 * - **Siempre montada.** Una región que nace junto a su contenido no produce ninguna mutación que
 *   el lector de pantalla pueda detectar, así que no se anuncia. Móntala fuera de los bloques
 *   condicionales de la vista y pásale `''` mientras no haya nada que decir.
 * - **El mensaje es su único contenido.** Una región anuncia cuanto contiene: envolver con ella la
 *   lista o el skeleton haría que el lector leyera la lista entera en cada cambio.
 *
 * Es un `<div role="status">` y no un `<output>`, aunque SonarQube S6819 prefiera el elemento
 * nativo: con VoiceOver en Safari (macOS 26.5) los cambios de un `<output>` se leen dos veces o
 * ninguna, y los de un `div` con `role="status"` una sola vez (comprobado a mano en el chat del
 * tutor). Busca la región por su rol (`getByRole('status')`), no por etiqueta.
 *
 * `aria-live="polite"` es redundante con el rol y se mantiene a propósito: es la parte del
 * contrato que un lector de pantalla antiguo interpreta sin mapear roles.
 */
function LiveStatusRegion({ message }: Readonly<LiveStatusRegionProps>): JSX.Element {
  return (
    <div aria-live="polite" className="visually-hidden" role="status">
      {message}
    </div>
  );
}

export default LiveStatusRegion;
